import { Box, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { generateLlmDqRules, validateLlmDqRules } from "../../../api's/LlmDqRulesApi";
import LoadingIcon from '../../../reusable-component/LoadingIcon';
import AddCustomRuleModal from './AddCustomRuleModal';
import AddPredefinedRuleModal from './AddPredefinedRuleModal';
import CustomComponent from './CustomComponent';

function LlmDqRules({
  disableForm,
  headerName,
  setHeaderName,
  columnName,
  formattedFields,
  setFormattedFields,
  dqRules,
  setDqRules,
  metricList,
}) {
  const [open, setOpen] = useState(false);
  const [openPredefinedRuleModal, setOpenPredefinedRuleModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [loading, setLoading] = useState(0);
  const handleOpenPredefinedRuleModal = () => setOpenPredefinedRuleModal(true);
  const handleClosePredefinedRuleModal = () => setOpenPredefinedRuleModal(false);

  useEffect(() => {
    const newFormattedFields = headerName.map((item) => ({
      fieldName: item.header,
      fieldValues: [item.sampleValue1, item.sampleValue2],
    }));
    const prevRuleFailedData = formattedFields.filter(
      (field) => !dqRules.some((rule) => rule.fieldName === field.fieldName && !rule?.isCustomOrPredefined)
    );

    const customOrPredefinedRecords = dqRules.filter((rule) => rule?.isCustomOrPredefined);
    setFormattedFields(newFormattedFields);
    const changes = compareArrays(formattedFields, newFormattedFields);

    formattedFields.length === 0 && dqRules.length === 0 && customOrPredefinedRecords.length === 0
      ? handleGeneratingRules(newFormattedFields)
      : prevRuleFailedData.length !== 0
      ? handleGenerateRulesForPrevRuleFailedData(prevRuleFailedData)
      : newFormattedFields.length === formattedFields.length &&
        newFormattedFields.length === changes.common.length &&
        customOrPredefinedRecords.length
      ? console.log(newFormattedFields.length, formattedFields.length, changes.common.length)
      : newFormattedFields.length !== changes.common.length && customOrPredefinedRecords.length > 0
      ? handleGenerateRulesForModifiedOrNewlyAddedorCustomPredefined(changes, customOrPredefinedRecords)
      : newFormattedFields.length !== changes.common.length
      ? handleGenerateRulesForModifiedOrNewlyAdded(changes)
      : changes.removedFieldName.length > 0
      ? setDqRules(
          dqRules?.filter(
            (rule) =>
              !changes.removedFieldName.some(
                (filter) => filter.fieldName === rule.fieldName && !rule.isCustomOrPredefined
              )
          )
        )
      : null;
  }, [headerName]);

  const compareArrays = (arr1, arr2) => {
    const changes = {
      added: [],
      removed: [],
      modified: [],
      common: [],
      commonFieldName: [],
      removedFieldName: [],
      addedModified: [],
    };

    // Check for added and modified fields
    arr2.forEach((el2) => {
      const el1 = arr1.find((el1) => el1.fieldName === el2.fieldName);
      if (!el1) {
        changes.added.push(el2);
        changes.addedModified.push(el2); // Add to addedModified
      } else {
        if (JSON.stringify(el1.fieldValues) === JSON.stringify(el2.fieldValues)) {
          changes.common.push(el1);
          changes.commonFieldName.push({ fieldName: el2.fieldName });
        } // Add to common
        if (JSON.stringify(el1.fieldValues) !== JSON.stringify(el2.fieldValues)) {
          changes.modified.push({
            fieldName: el2.fieldName,
            fieldValues: el2.fieldValues,
          });
          changes.addedModified.push({
            fieldName: el2.fieldName,
            fieldValues: el2.fieldValues,
          }); // Add modified fields to addedModified
        }
      }
    });

    // Check for removed fields
    arr1.forEach((el1) => {
      const el2 = arr2.find((el2) => el2.fieldName === el1.fieldName);
      if (!el2) {
        changes.removed.push(el1);
        changes.removedFieldName.push({ fieldName: el1.fieldName });
      }
    });

    return changes;
  };

  const handleGeneratingRules = async (newFormattedFields) => {
    const results = [];
    let failedRules = 0;
    setLoading(newFormattedFields.length); // Initialize loading count based on the number of fields

    try {
      const promises = newFormattedFields.map(async (data) => {
        try {
          // Generate rule for each field
          const response = await generateLlmDqRules(data);
          if (response.status === 200) {
            setLoading((loading) => loading - 1);

            // Validation data setup
            const validationData = {
              udf_code: response.data.udfCode,
              sample_values: data.fieldValues,
              field: data.fieldName,
            };

            // Validate the rule
            const validateResponse = await validateLlmDqRules(validationData);
            if (validateResponse.status === 200) {
              response.data['validationStatus'] = validateResponse.data.success ? 'success' : 'failed';
            }
            results.push(response.data);
          } else {
            setLoading((loading) => loading - 1);
          }
        } catch (error) {
          // Handle individual field error
          console.error('Error processing field:', data.fieldName, error);
          setLoading((loading) => loading - 1);
          failedRules++;
        }
      });

      await Promise.all(promises); // Wait for all promises to complete

      // Set rules if any were successfully created
      setDqRules(results);
      newFormattedFields.length !== failedRules &&
        enqueueSnackbar('Rules added successfully!', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });

      failedRules !== 0 &&
        enqueueSnackbar(`${failedRules} Rule failed!`, {
          variant: 'warning',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
    } catch (error) {
      // Handle any unexpected error
      setLoading((loading) => loading - 1);
      enqueueSnackbar('Error while creating rules!', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setLoading(0); // Reset loading count once all operations are done
    }
  };

  const handleGenerateRulesForModifiedOrNewlyAddedorCustomPredefined = async (ruleData) => {
    const results = [];
    let failedRules = 0;
    setLoading(ruleData.addedModified.length);
    try {
      const promises = ruleData.addedModified.map(async (data) => {
        try {
          // Attempt to generate rules
          const response = await generateLlmDqRules(data);
          if (response.status === 200) {
            setLoading((loading) => loading - 1);
            // Prepare validation data and call validation
            const validationData = {
              udf_code: response.data.udfCode,
              sample_values: data.fieldValues,
              field: data.fieldName,
            };

            // Validate response
            const validateResponse = await validateLlmDqRules(validationData);
            if (validateResponse.status === 200) {
              response.data['validationStatus'] = validateResponse.data.success ? 'success' : 'failed';
            } else {
              response.data['validationStatus'] = 'failed';
            }

            // Push to results and update combined data
            results.push(response.data);

            const filteredRecords = dqRules.filter((rule) =>
              ruleData.commonFieldName.some((common) => common.fieldName === rule.fieldName)
            );

            const customOrPredefinedRecords = dqRules.filter((rule) => rule?.isCustomOrPredefined);

            const combinedResult = [...results, ...filteredRecords, ...customOrPredefinedRecords];
            setDqRules(combinedResult);
          }
        } catch (generateError) {
          console.error('Error generating rule:', generateError);
          setLoading((loading) => loading - 1);
          failedRules++;
        }
      });

      await Promise.all(promises);

      ruleData.addedModified.length !== failedRules &&
        enqueueSnackbar('Rules added successfully!', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });

      failedRules !== 0 &&
        enqueueSnackbar(`${failedRules} Rule failed!`, {
          variant: 'warning',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
    } catch (error) {
      setLoading((loading) => loading - 1);
      console.error('Unexpected error in handleGenerateRulesForModifiedOrNewlyAddedorCustomPredefined:', error);
    } finally {
      // Decrement loading for each completed call
      setLoading(0);
    }
  };

  const handleGenerateRulesForModifiedOrNewlyAdded = async (ruleData) => {
    const results = [];
    let failedRules = 0;
    setLoading(ruleData.addedModified.length);

    try {
      const promises = ruleData.addedModified.map(async (data) => {
        try {
          // Attempt to generate LLM DQ rules
          const response = await generateLlmDqRules(data);
          if (response.status === 200) {
            setLoading((loading) => loading - 1);
            const validationData = {
              udf_code: response.data.udfCode,
              sample_values: data.fieldValues,
              field: response.data.fieldName,
            };

            // Attempt to validate the generated rules
            const validateResponse = await validateLlmDqRules(validationData);
            if (validateResponse.status === 200) {
              response.data['validationStatus'] = validateResponse.data.success ? 'success' : 'failed';
            } else {
              response.data['validationStatus'] = 'failed';
            }

            results.push(response.data);

            // Filter and combine results
            const filteredRecords = dqRules?.filter((rule) =>
              ruleData.commonFieldName.some((common) => common.fieldName === rule.fieldName)
            );
            const combinedResult = [...results, ...filteredRecords];
            setDqRules(combinedResult);
          }
        } catch (generateError) {
          setLoading((loading) => loading - 1);
          failedRules++;
          console.error('Error generating rule:', generateError);
        }
      });

      await Promise.all(promises);

      ruleData.addedModified.length !== failedRules &&
        enqueueSnackbar('Rules added successfully!', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });

      failedRules !== 0 &&
        enqueueSnackbar(`${failedRules} Rule failed!`, {
          variant: 'warning',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
    } catch (error) {
      setLoading((loading) => loading - 1);
      enqueueSnackbar('Error while creating rules!', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      console.error('Unexpected error in rule generation process:', error);
    } finally {
      // Decrement loading count for each completed call
      setLoading(0);
    }
  };

  const handleGenerateRulesForPrevRuleFailedData = async (ruleData) => {
    const results = [];
    let failedRules = 0;
    setLoading(ruleData.length);

    try {
      const promises = ruleData.map(async (data) => {
        try {
          // Attempt to generate LLM DQ rules
          const response = await generateLlmDqRules(data);
          if (response.status === 200) {
            setLoading((loading) => loading - 1);
            const validationData = {
              udf_code: response.data.udfCode,
              sample_values: data.fieldValues,
              field: response.data.fieldName,
            };

            // Attempt to validate the generated rules
            const validateResponse = await validateLlmDqRules(validationData);
            if (validateResponse.status === 200) {
              response.data['validationStatus'] = validateResponse.data.success ? 'success' : 'failed';
            } else {
              response.data['validationStatus'] = 'failed';
            }

            results.push(response.data);

            const combinedResult = [...results, ...dqRules];
            setDqRules(combinedResult);
          }
        } catch (generateError) {
          setLoading((loading) => loading - 1);
          failedRules++;
          console.error('Error while generating rule:', generateError);
        }
      });

      await Promise.all(promises);

      // Notify user of successful rule addition
      ruleData.length !== failedRules &&
        enqueueSnackbar('Rules added successfully!', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });

      failedRules !== 0 &&
        enqueueSnackbar(`${failedRules} Rule failed!`, {
          variant: 'warning',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
    } catch (error) {
      setLoading((loading) => loading - 1);
      enqueueSnackbar('Error while creating rules!', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      console.error('Unexpected error in rule generation process:', error);
    } finally {
      // Decrement loading count for each completed call
      setLoading(0);
    }
  };

  const handleGenerateRulesForEdit = async (ruleData, isCustomOrPredefined, filteredData) => {
    const results = [];
    const result = {
      fieldName: ruleData['fieldName'],
      fieldValues: ruleData['fieldValues'],
      statement: ruleData['statement'],
      metric: ruleData['metric'],
    };
    setLoading(1);
    try {
      const response = await generateLlmDqRules(result);
      let combinedResult = [];

      if (response.status === 200) {
        const validationData = {
          udf_code: response.data.udfCode,
          sample_values: result.fieldValues,
          field: result.fieldName,
        };
        const validateResponse = await validateLlmDqRules(validationData);
        setLoading(0);
        if (validateResponse.status === 200) {
          response.data['validationStatus'] = validateResponse.data.success ? 'success' : 'failed';
          response.data['isCustomOrPredefined'] = isCustomOrPredefined;
        }
        results.push(response.data);

        if (filteredData) {
          const filteredRecords = dqRules?.filter((rule) => rule.fieldId !== ruleData.fieldId);

          combinedResult = [...results, ...filteredRecords];
          enqueueSnackbar('Rule updated successfully!', {
            variant: 'success',
            autoHideDuration: 3000,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          });
        } else {
          combinedResult = [...results, ...dqRules];
          enqueueSnackbar('Rule added successfully!', {
            variant: 'success',
            autoHideDuration: 3000,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          });
        }
        setDqRules(combinedResult);
      }
    } catch (error) {
      enqueueSnackbar('500 Internal Server Error!', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      setLoading(0);
      console.error('Error in API call:', error);
    } finally {
      // Decrement loading for each completed call
      setLoading(0);
    }
  };

  return (
    <>
      {dqRules.map((dqRule, index) => (
        <CustomComponent
          index={index}
          dqRule={dqRule}
          key={dqRule.fieldId}
          dqRules={dqRules}
          setDqRules={setDqRules}
          handleGenerateRulesForEdit={handleGenerateRulesForEdit}
          formattedFields={formattedFields}
          setFormattedFields={setFormattedFields}
          headerName={headerName}
          setHeaderName={setHeaderName}
          metricList={metricList}
          disableForm={disableForm}
        />
      ))}

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button
          variant="contained"
          sx={{
            display: 'flex',
            marginTop: '8px',
            alignContent: 'flex-start',
            backgroundColor: '#00043C',
            '&:hover': { backgroundColor: '#00043C' },
            marginLeft: '17px',
          }}
          onClick={handleOpen}
          disabled={disableForm}
        >
          Add Custom Rules
        </Button>
        <Button
          variant="contained"
          sx={{
            display: 'flex',
            marginTop: '8px',
            alignContent: 'flex-start',
            backgroundColor: '#00043C',
            '&:hover': { backgroundColor: '#00043C' },
            marginLeft: '17px',
          }}
          onClick={handleOpenPredefinedRuleModal}
          disabled={disableForm}
        >
          Add Predefined Rules
        </Button>
      </div>

      {open && (
        <AddCustomRuleModal
          open={open}
          handleClose={handleClose}
          metricList={metricList}
          columnName={columnName}
          handleGenerateRulesForEdit={handleGenerateRulesForEdit}
        />
      )}
      {openPredefinedRuleModal && (
        <AddPredefinedRuleModal
          open={openPredefinedRuleModal}
          handleClose={handleClosePredefinedRuleModal}
          metricList={metricList}
          columnName={columnName}
          handleGenerateRulesForEdit={handleGenerateRulesForEdit}
        />
      )}
      {loading !== 0 && <LoadingIcon />}
    </>
  );
}

LlmDqRules.propTypes = {
  disableForm: PropTypes.bool,
  headerName: PropTypes.array,
};

export default LlmDqRules;
